/**
 * Speech-to-Text (STT) Provider Interface
 *
 * Base class for all STT implementations.
 * Subclasses must implement the abstract methods.
 */

export class STTProvider {
  constructor(name) {
    this.name = name;
    this.initialized = false;
    this.loading = false;
    this.language = 'en';

    // Callbacks
    this.onLoadingProgress = null;
    this.onTranscriptionStart = null;
    this.onTranscriptionEnd = null;
    this.onPartialTranscription = null;
  }

  /**
   * Get the name of this provider
   * @returns {string}
   */
  getName() {
    return this.name;
  }

  /**
   * Check if this provider is supported on the current device
   * @param {DeviceCapabilities} capabilities
   * @returns {boolean}
   */
  static isSupported(capabilities) {
    throw new Error('isSupported() must be implemented by subclass');
  }

  /**
   * Check if this provider supports a given language
   * @param {string} language - Language code (e.g., 'en', 'it')
   * @returns {boolean}
   */
  static supportsLanguage(language) {
    throw new Error('supportsLanguage() must be implemented by subclass');
  }

  /**
   * Initialize the STT engine
   * Must be called before transcribe()
   * @param {Object} options - Configuration options
   * @returns {Promise<void>}
   */
  async initialize(options = {}) {
    throw new Error('initialize() must be implemented by subclass');
  }

  /**
   * Transcribe audio to text
   * @param {Float32Array} audio - Audio samples at 16kHz
   * @param {Object} options - Transcription options
   * @returns {Promise<{text: string, segments?: Array}>}
   */
  async transcribe(audio, options = {}) {
    throw new Error('transcribe() must be implemented by subclass');
  }

  /**
   * Get the sample rate expected by this provider
   * @returns {number}
   */
  getSampleRate() {
    return 16000; // Most models expect 16kHz
  }

  /**
   * Check if the provider is ready to transcribe
   * @returns {boolean}
   */
  isReady() {
    return this.initialized && !this.loading;
  }

  /**
   * Check if the model is currently loading
   * @returns {boolean}
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Get loading progress (0-100)
   * @returns {number}
   */
  getLoadingProgress() {
    return 0;
  }

  /**
   * Set the language for transcription
   * @param {string} language - Language code
   */
  setLanguage(language) {
    this.language = language;
  }

  /**
   * Clean up resources
   * @returns {Promise<void>}
   */
  async destroy() {
    throw new Error('destroy() must be implemented by subclass');
  }
}

export default STTProvider;
